import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import Icon, { IconName } from '@/components/ui/Icon';
import { cn } from '@/lib/utils';

export default function ImageUploadButton({
  size,
  onFileSelect,
  onButtonClick,
  replaceImage = false,
  className,
  ref,
}: {
  size?: number;
  onFileSelect: (file: File) => void;
  onButtonClick?: () => void;
  replaceImage?: boolean;
  className?: string;
  ref?: React.RefObject<{ triggerFileInput: () => void }>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Expose the triggerFileInput method through the ref
  if (ref) {
    ref.current = {
      triggerFileInput: () => inputRef.current?.click(),
    };
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
  };
  const t = useTranslations('PhotoUploadDialog');
  return (
    <div className={cn('relative rounded-full', className)}>
      {replaceImage ? (
        <Button
          variant='ghost'
          className='h-auto w-auto rounded-full border-none px-0 py-0'
        >
          <Icon variant={IconName.Replace} width={16} height={16} />
          <span className='text-xs'>{t('replace')}</span>
        </Button>
      ) : (
        <CameraButton size={size} />
      )}
      <input
        ref={inputRef}
        type='file'
        id='file-upload'
        className='absolute inset-0 h-full w-full cursor-pointer opacity-0'
        onChange={handleFileChange}
        onClick={onButtonClick}
        accept='image/*;capture=camera'
      />
    </div>
  );
}

export const CameraButton = ({
  size,
  onClick,
  className,
}: {
  size?: number;
  onClick?: () => void;
  className?: string;
}) => {
  const t = useTranslations('PhotoUploadDialog');
  return (
    <div className={cn('relative rounded-full hover:opacity-80', className)}>
      <Button
        variant='ghost'
        style={size ? { width: size, height: size } : undefined}
        className='border-black-100 bg-white-300 rounded-full border-2 border-dashed p-0'
        onClick={onClick}
      >
        <Icon variant={IconName.Camera} width={32} height={32} />
        <span className='sr-only'>{t('upload')}</span>
      </Button>
    </div>
  );
};
